import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const GAP = 12
const EDGE = 8
const PAD = 6

function sameRect(a, b) {
  if (!a || !b) return a === b
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height
}

function measure(selector) {
  if (!selector) return null
  const el = document.querySelector(selector)
  if (!el) return null
  const r = el.getBoundingClientRect()
  if (!r.width || !r.height) return null
  return { top: r.top, left: r.left, width: r.width, height: r.height }
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max))

// Below, above, right, then left of the target; the first that fits wins. A
// target too big for any side (a whole pane) gets the card inside its corner.
function place(rect, size) {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const { width: w, height: h } = size
  if (!rect) return { top: (vh - h) / 2, left: (vw - w) / 2 }
  const centerX = clamp(rect.left + rect.width / 2 - w / 2, EDGE, vw - w - EDGE)
  const centerY = clamp(rect.top + rect.height / 2 - h / 2, EDGE, vh - h - EDGE)
  const below = rect.top + rect.height + GAP
  if (below + h <= vh - EDGE) return { top: below, left: centerX }
  const above = rect.top - GAP - h
  if (above >= EDGE) return { top: above, left: centerX }
  const right = rect.left + rect.width + GAP
  if (right + w <= vw - EDGE) return { top: centerY, left: right }
  const left = rect.left - GAP - w
  if (left >= EDGE) return { top: centerY, left }
  return {
    top: clamp(rect.top + 16, EDGE, vh - h - EDGE),
    left: clamp(rect.left + 16, EDGE, vw - w - EDGE),
  }
}

// A help card pointing at `target` (a selector), or centered without one.
// `dim` darkens everything except the target (tours); otherwise the target
// gets a soft ring (tips). The target is re-measured while open, so the card
// follows resizes and scrolling.
function HelpBubble({ target, dim, onEscape, label, children }) {
  const [rect, setRect] = useState(() => measure(target))
  const [size, setSize] = useState({ width: 320, height: 170 })
  const cardRef = useRef(null)

  useEffect(() => {
    const update = () => {
      const next = measure(target)
      setRect((current) => (sameRect(current, next) ? current : next))
    }
    update()
    const id = setInterval(update, 250)
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      clearInterval(id)
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [target])

  useLayoutEffect(() => {
    const card = cardRef.current
    if (!card) return undefined
    const update = () => {
      const next = { width: card.offsetWidth, height: card.offsetHeight }
      setSize((current) =>
        current.width === next.width && current.height === next.height ? current : next
      )
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(card)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!onEscape) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape') onEscape()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onEscape])

  const position = place(rect, size)
  const ring = rect && {
    top: rect.top - PAD,
    left: rect.left - PAD,
    width: rect.width + PAD * 2,
    height: rect.height + PAD * 2,
  }

  return createPortal(
    <>
      {dim && !ring ? <div className="help-backdrop" /> : null}
      {ring ? <div className={dim ? 'help-spotlight' : 'help-ring'} style={ring} /> : null}
      <div
        ref={cardRef}
        className="help-card"
        role="dialog"
        aria-label={label}
        style={{ top: position.top, left: position.left }}
      >
        {children}
      </div>
    </>,
    document.body
  )
}

export default HelpBubble
